/**
 * CardFormFieldsV2 — V2 port of CardFormFields. Same prop shape:
 * caller owns `card` + `handleChange`, this renders the fields.
 *
 * Sections:
 *   01 Basic info       — title + Active toggle
 *   02 Card images      — front + back UploadTiles
 *   03 Sharing          — share image + universal link
 *   04 Linked content   — Web / Android / iOS deeplink URLs +
 *                         button text + icon picker (media resource
 *                         autocomplete kept from v1) + preview
 *
 * Image uploads route through v1's `uploadImage(file, target)` so
 * `mediaStore.image` + `mediaStore.target` settle into the right
 * card field.
 */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { uploadImage, clearMedia } from '../../../../../actions/mediaActions';
import LinkedContentButton from '../LinkedContentButton';

import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  ToggleSwitch,
  LinkField,
  UploadTile,
  SearchSelect,
} from '../../../../redesign/edit';

export default function CardFormFieldsV2({ card, handleChange }) {
  const dispatch = useDispatch();
  const mediaResources = useSelector((s) => s.mediaResources?.mediaResources) || [];
  const mediaResourceLoading = useSelector((s) => s.mediaResources?.loading);
  const mediaStore = useSelector((s) => s.media);

  /* Media-resource options for the icon picker — value is the
     resource_url (what the card stores), with a thumbnail + name +
     description per row (the rich layout the MUI Autocomplete had). */
  const mediaOptions = useMemo(
    () =>
      mediaResources.map((r) => ({
        value: r.resource_url,
        label: r.name,
        sublabel: r.description,
        image: r.resource_url,
      })),
    [mediaResources],
  );

  /* Media settle — uploaded image flows back into card[target]. */
  useEffect(() => {
    if (mediaStore?.image && mediaStore?.target) {
      handleChange({ target: { name: mediaStore.target, value: mediaStore.image } });
      dispatch(clearMedia());
    }
  }, [mediaStore, dispatch, handleChange]);

  const handleImageUpload = (name) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    dispatch(uploadImage(file, name));
  };

  const removeImage = (name) => () => {
    handleChange({ target: { name, value: '' } });
  };

  const handleToggle = (name) => (e) =>
    handleChange({ target: { name, value: !!e.target.checked } });

  return (
    <>
      {/* 01 Basic info */}
      <FormSection title="Basic info" num="01">
        <FieldRow cols={2} minColWidth={240}>
          <FieldBlock label="Title" htmlFor="f-card-title">
            <TextInput
              id="f-card-title"
              name="title"
              value={card.title || ''}
              onChange={handleChange}
              autoFocus
            />
          </FieldBlock>
          <FieldBlock label="Status" noMargin>
            <div style={{ paddingTop: 6 }}>
              <ToggleSwitch
                name="is_active"
                checked={!!card.is_active}
                onChange={handleToggle('is_active')}
                label="Active"
              />
            </div>
          </FieldBlock>
        </FieldRow>
      </FormSection>

      {/* 02 Card images */}
      <FormSection title="Card images" num="02">
        <FieldRow cols={2} minColWidth={240}>
          <UploadTile
            label="Front image"
            value={card.front_image_url || ''}
            accept="image/*"
            onChange={handleImageUpload('front_image_url')}
            onRemove={removeImage('front_image_url')}
          />
          <UploadTile
            label="Back image"
            value={card.back_image_url || ''}
            accept="image/*"
            onChange={handleImageUpload('back_image_url')}
            onRemove={removeImage('back_image_url')}
          />
        </FieldRow>
      </FormSection>

      {/* 03 Sharing */}
      <FormSection title="Sharing" num="03">
        <FieldRow cols={2} minColWidth={240}>
          <UploadTile
            label="Share image"
            value={card.share_image_url || ''}
            accept="image/*"
            onChange={handleImageUpload('share_image_url')}
            onRemove={removeImage('share_image_url')}
          />
          <FieldBlock label="Universal Link" hint="Generated automatically · share & deep links">
            <LinkField value={card.universal_link || ''} />
          </FieldBlock>
        </FieldRow>
      </FormSection>

      {/* 04 Linked content */}
      <FormSection title="Linked content" num="04">
        <FieldRow cols={3} minColWidth={180}>
          <FieldBlock label="Link (Web)" htmlFor="f-card-web">
            <TextInput
              id="f-card-web"
              name="target_web_deeplink_url"
              value={card.target_web_deeplink_url || ''}
              onChange={handleChange}
            />
          </FieldBlock>
          <FieldBlock label="Link (Android)" htmlFor="f-card-and">
            <TextInput
              id="f-card-and"
              name="target_android_deeplink_url"
              value={card.target_android_deeplink_url || ''}
              onChange={handleChange}
            />
          </FieldBlock>
          <FieldBlock label="Link (iOS)" htmlFor="f-card-ios">
            <TextInput
              id="f-card-ios"
              name="target_ios_deeplink_url"
              value={card.target_ios_deeplink_url || ''}
              onChange={handleChange}
            />
          </FieldBlock>
        </FieldRow>

        <FieldRow cols={2} minColWidth={240}>
          <FieldBlock label="Button text" htmlFor="f-card-btn-text">
            <TextInput
              id="f-card-btn-text"
              name="linked_content_button_text"
              value={card.linked_content_button_text || ''}
              onChange={handleChange}
            />
          </FieldBlock>
          <FieldBlock label="Button icon (media resource)">
            <SearchSelect
              id="card-icon-select"
              value={card.linked_content_button_icon_url || null}
              onChange={(value) =>
                handleChange({ target: { name: 'linked_content_button_icon_url', value: value || '' } })
              }
              options={mediaOptions}
              placeholder="Select a media resource"
              searchPlaceholder="Search media resources…"
              emptyText={mediaResourceLoading ? 'Loading media resources…' : 'No media resources found'}
            />
          </FieldBlock>
        </FieldRow>

        <FieldBlock label="Button preview" noMargin>
          <LinkedContentButton
            sx={{ background: '#FFFCF5' }}
            card={card}
            title=""
          />
        </FieldBlock>
      </FormSection>
    </>
  );
}
